import React from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import FormLayout from "components/form/FormLayout";
import Toggle from "components/form/toggle/Toggle";
import { IPutTeeSheetSettings } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import Divider from "components/divider";
import Input from "components/form/input/Input";
import SelectNew from "components/select/SelectNew";
import { TFunction } from "i18next";
import TextEditor from "components/textEditor/textEditor";

interface IProps {
  state: IPutTeeSheetSettings;
  updateState: <K extends keyof IPutTeeSheetSettings>(key: K, value: IPutTeeSheetSettings[K]) => void;
}

const depositApplicationOptions = (t: TFunction) => [
  { label: t("Player"), id: "player" },
  { label: t("Booking"), id: "booking" },
];

const rainCheckExpirationOptions = (t: TFunction) => [
  { label: t("One Year"), option: "one_year" },
  { label: t("End of Year"), option: "end_of_year" },
  { label: t("Custom Months"), option: "custom_months" },
  { label: t("No Expiration"), option: "no_expiry" },
];

export default function PaymentsAndDeposits(props: IProps) {
  const { state, updateState } = props;
  const { t } = useTranslation();
  const { Option } = Select;
  const isCustomMonths = state.rain_check_expiry === "custom_months";

  function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    updateState(id as keyof IPutTeeSheetSettings, checked);
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    updateState(id as keyof IPutTeeSheetSettings, value === "" || Number(value) < 0 ? "" : Number(value));
  }

  return (
    <div className="p-2">
      <FormLayout>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Account Payments")}
            id="web_display_payment_option_account"
            checked={state.web_display_payment_option_account || false}
            onChange={handleToggleChange}
            labelSupport={t("Allow players to pay with their account on the public booking portal")}
          />
          <Toggle
            labelRight={t("Gift Card Payments")}
            id="web_display_payment_option_gift_card"
            checked={state.web_display_payment_option_gift_card || false}
            onChange={handleToggleChange}
            labelSupport={t("Allow players to pay with gift cards on the public booking portal")}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Credit Book Payments")}
            id="web_display_payment_option_credit_book"
            checked={state.web_display_payment_option_credit_book || false}
            onChange={handleToggleChange}
            labelSupport={t("Allow players to pay with their credit book on the public booking portal")}
          />
        </FormLayout.Group>
        <Divider />
        <FormLayout.Group>
          <Toggle
            labelRight={t("Pre paid optional")}
            id="tee_times_pre_paid_option"
            checked={state.tee_times_pre_paid_option || false}
            onChange={handleToggleChange}
            labelSupport={t("Allow players to pre pay for their tee time")}
          />
        </FormLayout.Group>
        <FormLayout.Group>
          <Toggle
            labelRight={t("Auto refund pre paid bookings")}
            id="auto_refund_pre_paid_bookings"
            checked={state.auto_refund_pre_paid_bookings || false}
            onChange={handleToggleChange}
            labelSupport={t("Automatically refund pre paid bookings when they are cancelled")}
          />
        </FormLayout.Group>
      </FormLayout>
      <Divider />
      <FormLayout>
        <FormLayout.Group>
          <Toggle
            labelRight={"Enable tee time deposits"}
            id="enable_tee_time_deposits"
            checked={!!state.enable_tee_time_deposits || false}
            onChange={handleToggleChange}
          />
        </FormLayout.Group>
        {state?.enable_tee_time_deposits ? (
          <FormLayout.Group>
            <Input
              label={t("Deposit amount")}
              onChange={handleInputChange}
              id="deposit_amount"
              value={state?.deposit_amount}
              type="number"
            />
            <SelectNew
              id="deposit_application_select"
              label={t("Deposit application")}
              options={depositApplicationOptions(t)}
              value={state?.deposit_application}
              tooltip={t("Apply deposit amount to a single player or the entire booking")}
              onChange={(value: string) => updateState("deposit_application", value)}
            />
          </FormLayout.Group>
        ) : null}
      </FormLayout>
      <Divider />
      <FormLayout>
        <FormLayout.Group alignStart>
          <Select
            label={t("Rain check expiry")}
            defaultValue={state.rain_check_expiry ?? ""}
            onChange={(value: "one_year" | "end_of_year" | "no_expiry" | "custom_months") =>
              updateState("rain_check_expiry", value)
            }
            helpText={t("Select when the rain check will expire")}
          >
            {rainCheckExpirationOptions(t).map((option, index) => (
              <Option key={index} value={option.option}>
                {option.label}
              </Option>
            ))}
          </Select>
          {isCustomMonths ? (
            <Input
              type="number"
              label={t("Rain check expiry months")}
              value={state.rain_check_expiry_months ?? 6}
              onChange={e => updateState("rain_check_expiry_months", Number(e.target.value))}
              min={1}
              max={99}
              helpText={t("Number of months until the rain check expires")}
            />
          ) : null}
        </FormLayout.Group>
        <FormLayout.Group>
          <TextEditor
            label={t("Rain check description")}
            markdownText={state.rain_check_description || ""}
            markdownTextOnChange={text => updateState("rain_check_description", text)}
          />
        </FormLayout.Group>
      </FormLayout>
    </div>
  );
}
